<template>
    <div
        class="c-accordion"
        :class="{
            'is-open': isOpen,
            'c-accordion--upside-down': upsideDown
        }"
    >
        <header
            class="c-accordion__header"
            @click="isOpen ? close() : open()"
            v-cursor
        >
            <slot name="title">
                <h2
                    v-if="title"
                    class="c-accordion__title || t-t2-serif"
                >
                    {{ title }}
                </h2>
            </slot>
            <button
                class="c-accordion__btn"
                aria-label="Toggle dropdown"
            >
                <icon icon="arrow-stroke-down" />
            </button>
        </header>
        <div
            ref="container"
            class="c-accordion__container"
            :class="{ 'has-scroll' : hasScroll }"
        >
            <div ref="inner" class="c-accordion__content">
                <slot>
                    <anim-text
                        :cms="true"
                        :text="content"
                        :reveal="false"
                        :visible="isOpen"
                    />
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from 'objects/Icon';
import AnimText from 'objects/AnimText';

import debounce from 'lodash/debounce';
import gsap from 'gsap';

export default {
    name: 'Accordion',
    components: {
        Icon,
        AnimText,
    },
    data: () => ({
        isOpen: false,
        isActive: true,
        hasScroll: false,
        opts: {
            scroll: false,
            watchCSS: false,
            maxHeight: false,
            open: false
        }
    }),
    props: {
        title: {
            type: String,
            default: null,
        },
        triggerLabel: {
            type: String,
            default: null,
        },
        content: {
            type: String,
            default: null,
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        upsideDown: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        this.opts = Object.assign(this.opts, this.options)
        this.isOpen = this.opts.open
    },
    mounted() {
        // Window event
        window.addEventListener('resize', debounce(this.watchCSS, 100));
    },
    methods: {
        open() {
            if (!this.isActive) {
                return;
            }

            this.isOpen = true;

            const height = this.$refs.inner.offsetHeight;

            gsap.to(this.$refs.container, {
                duration: 1,
                height: this.opts.maxHeight && height > this.opts.maxHeight ? this.opts.maxHeight : height,
                opacity: 1,
                onStart: () => {
                    if (height === this.opts.maxHeight) {
                        this.hasScroll = true;
                    }
                },
                onComplete: () => {
                    gsap.set(this.$refs.container, {
                        height: 'auto'
                    });
                },
                ease: 'expo.inOut',
            });

            if (this.opts.scroll) {
                const scrollY = this.$refs.container.getBoundingClientRect().top + window.scrollY;
                this.animScroll(scrollY);
            }
        },
        close() {
            if (!this.isActive) {
                return;
            }

            this.isOpen = false;

            gsap.to(this.$refs.container, {
                duration: 1,
                height: 0,
                opacity: 0,
                onComplete: () => {
                    gsap.set(this.$refs.container, {
                        height: 0
                    });
                    this.hasScroll = false;
                },
                ease: 'expo.inOut',
            });
        },
        toggle() {
            if (this.isOpen) {
                this.close();
            } else {
                this.open();
            }
        },
        animScroll(y) {
            gsap.to(window, {
                duration: 1,
                scrollTo: {
                    y: y,
                    autoKill: false,
                },
                ease: 'expo.inOut',
            });
        },

        watchCSS() {
            if (!this.opts.watchCSS) return;

            const afterContent = getComputedStyle(this.$el, ':after').content;

            if (afterContent.indexOf('accordion') !== -1) {
                this.isActive = true;
            } else {
                this.isActive = false;
                this.$refs.container.removeAttribute('style');
            }
        },
    },
    watch: {
        isOpen(open) {
            if(open) {
                this.open()
            } else {
                this.close()
            }
        }
    },
    beforeDestroy() {
        // Remove Window event
        window.removeEventListener('resize', debounce(this.watchCSS, 100));
    },
};
</script>

<style lang="scss">

.c-accordion {

    // Code to add to enable accordion if watchCSS is enabled
    // &:after {
    //     content: "accordion";
    //     display: none;
    // }

    &.is-open {

        .c-accordion__content {
            pointer-events: initial;
        }

        .c-accordion__btn .o-icon {
            transform: rotate(180deg);
        }
    }

    &--upside-down {
        display: flex;
        flex-direction: column;

        .c-accordion__header {
            order: 2
        }

        .c-accordion__container {
            order: 1
        }

        .c-accordion__btn {
            transform: rotate(180deg);
        }

        &.is-open {

            .c-accordion__btn {
                transform: rotate(360deg);
            }
        }
    }
}


.c-accordion__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1em;
    padding-bottom: 1em;
    user-select: none;
    cursor: pointer;

    &:after {
        @include pseudo-el($height: 1px, $bg: $color-grey);
        position: absolute;
        top: 100%;
        left: 0;
    }

    &:hover,
    .is-open & {
    }

    &:hover {
    }

    &:hover,
    .is-open &:hover {
    }

    .is-open & {

    }
}

.c-accordion__title {
    width: 100%;
    padding: 1em 1.5em;

    @media #{md("sm")} {
        padding-right: 2.5em;
        padding-left: 2.5em;
    }
}


.c-accordion__btn {
    display: flex;
    align-items: center;
    margin-left: 2em;
    transition: transform .2s $in-out-circ;

    .o-icon {
        transition: transform .3s $in-out-circ;
    }
}

.c-accordion__container {
    height: 0;
    opacity: 0;
    overflow: hidden;

    &.has-scroll {

        &:after {
            @include pseudo-el($height: 4em);
            position: absolute;
            bottom: 0;
            left: 0;
            @include linear-gradient(to top, $color-light, $color-light 1.5em, rgba(255, 255, 255, 0) 100%);
            pointer-events: none;
        }

        .c-accordion__content {
            padding-bottom: 3em;
        }
    }
}


.c-accordion__content {
    // max-height: 400px;
    padding-top: 2em;
    padding-bottom: 2em;
    pointer-events: none;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
}

</style>
